/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Download } from 'lucide-react';
import { createEvent } from 'ics';
import { saveAs } from 'file-saver';

interface CalendarExportProps {
	title: string;
	description: string;
	startDate: Date;
	startTime: string;
	duration: number;
	location: string;
}

export function CalendarExport({
	title,
	description,
	startDate,
	startTime,
	duration,
	location,
}: CalendarExportProps) {
	// Parse the time from the time slot (e.g., "14:00")
	const generateIcsFile = () => {
		const [hours, minutes] = startTime.split(':').map(Number);
		const start = new Date(startDate);
		start.setHours(hours, minutes, 0, 0);

		const end = new Date(start);
		end.setMinutes(start.getMinutes() + duration);

		const event = {
			start: [
				start.getFullYear(),
				start.getMonth() + 1,
				start.getDate(),
				hours,
				minutes,
			],
			end: [
				end.getFullYear(),
				end.getMonth() + 1,
				end.getDate(),
				end.getHours(),
				end.getMinutes(),
			],
			title,
			description,
			location,
			status: 'CONFIRMED',
			busyStatus: 'BUSY',
		};

		createEvent(event as any, (error, value) => {
			if (error) {
				console.error('Error generating iCalendar file:', error);
				return;
			}

			if (value) {
				const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' });
				saveAs(blob, `${title.replace(/\s+/g, '-')}.ics`);
			}
		});
	};

	const generateGoogleCalendarUrl = () => {
		const [hours, minutes] = startTime.split(':').map(Number);
		const start = new Date(startDate);
		start.setHours(hours, minutes, 0, 0);

		const end = new Date(start);
		end.setMinutes(start.getMinutes() + duration);

		const startISO = start.toISOString().replace(/-|:|\.\d+/g, '');
		const endISO = end.toISOString().replace(/-|:|\.\d+/g, '');

		const url = new URL('https://calendar.google.com/calendar/render');
		url.searchParams.append('action', 'TEMPLATE');
		url.searchParams.append('text', title);
		url.searchParams.append('details', description);
		url.searchParams.append('location', location);
		url.searchParams.append('dates', `${startISO}/${endISO}`);

		return url.toString();
	};

	const generateOutlookUrl = () => {
		const [hours, minutes] = startTime.split(':').map(Number);
		const start = new Date(startDate);
		start.setHours(hours, minutes, 0, 0);

		const end = new Date(start);
		end.setMinutes(start.getMinutes() + duration);

		const startISO = start.toISOString();
		const endISO = end.toISOString();

		const url = new URL(
			'https://outlook.office.com/calendar/0/deeplink/compose',
		);
		url.searchParams.append('subject', title);
		url.searchParams.append('body', description);
		url.searchParams.append('location', location);
		url.searchParams.append('startdt', startISO);
		url.searchParams.append('enddt', endISO);
		url.searchParams.append('path', '/calendar/action/compose');
		url.searchParams.append('rru', 'addevent');

		return url.toString();
	};

	const generateYahooCalendarUrl = () => {
		const [hours, minutes] = startTime.split(':').map(Number);
		const start = new Date(startDate);
		start.setHours(hours, minutes, 0, 0);

		const end = new Date(start);
		end.setMinutes(start.getMinutes() + duration);

		const url = new URL('https://calendar.yahoo.com');
		url.searchParams.append('title', title);
		url.searchParams.append('desc', description);
		url.searchParams.append('in_loc', location);
		url.searchParams.append(
			'st',
			start.toISOString().replace(/-|:|\.\d+/g, ''),
		);
		url.searchParams.append('et', end.toISOString().replace(/-|:|\.\d+/g, ''));
		url.searchParams.append('v', '60');

		return url.toString();
	};

	return (
		<div className='space-y-3'>
			<h3 className='font-medium'>Add to Calendar</h3>
			<div className='flex flex-wrap gap-2'>
				<Button
					variant='outline'
					size='sm'
					className='flex items-center gap-2'
					onClick={generateIcsFile}
				>
					<Download size={16} />
					<span>iCal</span>
				</Button>
				<Button
					variant='outline'
					size='sm'
					className='flex items-center gap-2'
					onClick={() => window.open(generateGoogleCalendarUrl(), '_blank')}
				>
					<svg
						xmlns='http://www.w3.org/2000/svg'
						x='0px'
						y='0px'
						width='16'
						height='16'
						viewBox='0 0 48 48'
					>
						<path
							fill='#c7c7c7'
							fillRule='evenodd'
							d='M38,5c-6.302,0-21.698,0-28,0C8.895,5,8,5.895,8,7 c0,3.047,0,3,0,3h32c0,0,0,0.047,0-3C40,5.895,39.105,5,38,5z M14,8c-0.552,0-1-0.448-1-1c0-0.552,0.448-1,1-1s1,0.448,1,1 C15,7.552,14.552,8,14,8z M34,8c-0.552,0-1-0.448-1-1c0-0.552,0.448-1,1-1s1,0.448,1,1C35,7.552,34.552,8,34,8z'
							clipRule='evenodd'
						></path>
						<path
							fill='#1976d2'
							fillRule='evenodd'
							d='M44,11c0.103-0.582-1.409-2-2-2C34.889,9,13.111,9,6,9 c-1,0-2.103,1.418-2,2c0.823,4.664,3,15,3,15h34C41,26,43.177,15.664,44,11z'
							clipRule='evenodd'
						></path>
						<path
							fill='#1e88e5'
							fillRule='evenodd'
							d='M41,26H7c0,0-2.177,10.336-3,15c0,1.146,0.792,2,2,2 c7.111,0,28.889,0,36,0c0.591,0,2-0.5,2-2C43.177,36.336,41,26,41,26z'
							clipRule='evenodd'
						></path>
						<path
							fill='#fafafa'
							fillRule='evenodd'
							d='M20.534 26c.984.325 1.687.85 2.105 1.557.433.732.65 1.55.65 2.457 0 1.582-.519 2.826-1.556 3.733-1.037.906-2.363 1.36-3.977 1.36-1.582 0-2.892-.427-3.93-1.282-1.038-.855-1.536-2.014-1.497-3.476l.036-.072h2.242c0 .914.28 1.642.841 2.182.56.541 1.33.811 2.308.811.994 0 1.773-.27 2.337-.811.564-.541.847-1.34.847-2.397 0-1.073-.25-1.864-.751-2.373-.501-.509-1.292-.763-2.373-.763h-2.051V26H20.534zM31.637 26H33.986000000000004V34.856H31.637z'
							clipRule='evenodd'
						></path>
						<path
							fill='#e0e0e0'
							fillRule='evenodd'
							d='M14.727 22.036h-2.254l-.024-.072c-.04-1.312.435-2.427 1.425-3.345.99-.918 2.284-1.377 3.882-1.377 1.606 0 2.886.427 3.84 1.282.954.855 1.431 2.073 1.431 3.655 0 .716-.217 1.429-.65 2.141-.433.712-1.083 1.254-1.95 1.628L20.534 26h-4.77v-.911h2.051c1.042 0 1.779-.26 2.212-.781.433-.521.65-1.246.65-2.176 0-.994-.246-1.749-.739-2.266-.493-.517-1.22-.775-2.182-.775-.914 0-1.648.268-2.2.805C15.022 20.414 14.746 21.098 14.727 22.036zM33.986 26L31.637 26 31.637 19.782 28.083 19.83 28.083 18.136 33.986 17.492z'
							clipRule='evenodd'
						></path>
						<path
							fill='#1976d2'
							fillRule='evenodd'
							d='M6 9c-1.438 0-2.103 1.418-2 2 .823 4.664 3 15 3 15M41 26c0 0 2.177-10.336 3-15 0-1.625-1.409-2-2-2'
							clipRule='evenodd'
						></path>
					</svg>
					<span>Google</span>
				</Button>
				<Button
					variant='outline'
					size='sm'
					className='flex items-center gap-2'
					onClick={() => window.open(generateOutlookUrl(), '_blank')}
				>
					<svg
						xmlns='http://www.w3.org/2000/svg'
						x='0px'
						y='0px'
						width='16'
						height='16'
						viewBox='0 0 48 48'
					>
						<path
							fill='#1976d2'
							d='M28,13h14.533C43.343,13,44,13.657,44,14.467v19.066C44,34.343,43.343,35,42.533,35H28V13z'
						></path>
						<rect
							width='14'
							height='15.542'
							x='28'
							y='17.958'
							fill='#fff'
						></rect>
						<polygon fill='#1976d2' points='27,44 4,39.5 4,8.5 27,4'></polygon>
						<path
							fill='#fff'
							d='M15.25,16.5c-3.176,0-5.75,3.358-5.75,7.5s2.574,7.5,5.75,7.5S21,28.142,21,24	S18.426,16.5,15.25,16.5z M15,28.5c-1.657,0-3-2.015-3-4.5s1.343-4.5,3-4.5s3,2.015,3,4.5S16.657,28.5,15,28.5z'
						></path>
						<rect
							width='2.7'
							height='2.9'
							x='28.047'
							y='29.737'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='31.448'
							y='29.737'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='34.849'
							y='29.737'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='28.047'
							y='26.159'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='31.448'
							y='26.159'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='34.849'
							y='26.159'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='38.25'
							y='26.159'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='28.047'
							y='22.706'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='31.448'
							y='22.706'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='34.849'
							y='22.706'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='38.25'
							y='22.706'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='31.448'
							y='19.112'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='34.849'
							y='19.112'
							fill='#1976d2'
						></rect>
						<rect
							width='2.7'
							height='2.9'
							x='38.25'
							y='19.112'
							fill='#1976d2'
						></rect>
					</svg>
					<span>Outlook</span>
				</Button>
				<Button
					variant='outline'
					size='sm'
					className='flex items-center gap-2'
					onClick={() => window.open(generateYahooCalendarUrl(), '_blank')}
				>
					<svg
						xmlns='http://www.w3.org/2000/svg'
						x='0px'
						y='0px'
						width='16'
						height='16'
						viewBox='0 0 48 48'
					>
						<path
							fill='#5c6bc0'
							d='M42,37c0,2.762-2.238,5-5,5H11c-2.761,0-5-2.238-5-5V11c0-2.762,2.239-5,5-5h26c2.762,0,5,2.238,5,5	V37z'
						></path>
						<path
							fill='#fff'
							d='M34,13H14c-0.552,0-1,0.448-1,1v12c0,0.552,0.448,1,1,1h20c0.552,0,1-0.448,1-1V14	C35,13.448,34.552,13,34,13z M33,17l-9,4l-9-4v-2h18V17z'
						></path>
						<g transform='matrix(.63072 0 0 .63072 -72.063 127.866)'>
							<path
								fill='#fff'
								d='M132.266-150.491h2.385l1.389,3.552l1.407-3.552h2.322l-3.496,8.41h-2.337 l0.957-2.228L132.266-150.491z'
							></path>
							<path
								fill='#fff'
								d='M142.186-150.633c-1.792,0-2.924,1.607-2.924,3.207c0,1.801,1.242,3.228,2.89,3.228 c1.23,0,1.693-0.749,1.693-0.749v0.584h2.08v-6.128h-2.08v0.557C143.846-149.934,143.328-150.633,142.186-150.633z M142.629-148.663c0.827,0,1.253,0.654,1.253,1.244c0,0.636-0.457,1.259-1.253,1.259c-0.66,0-1.256-0.539-1.256-1.232 C141.372-148.095,141.852-148.663,142.629-148.663z'
							></path>
							<path
								fill='#fff'
								d='M146.642-144.363v-8.834h2.175v3.284c0,0,0.517-0.719,1.599-0.719 c1.324,0,2.099,0.986,2.099,2.396v3.873h-2.159v-3.343c0-0.477-0.227-0.938-0.742-0.938c-0.524,0-0.797,0.468-0.797,0.938v3.343 H146.642z'
							></path>
							<path
								fill='#fff'
								d='M156.224-150.632c-2.052,0-3.274,1.56-3.274,3.232c0,1.902,1.479,3.203,3.281,3.203 c1.747,0,3.275-1.241,3.275-3.171C159.507-149.479,157.907-150.632,156.224-150.632z M156.244-148.645 c0.725,0,1.226,0.604,1.226,1.247c0,0.549-0.467,1.226-1.226,1.226c-0.695,0-1.217-0.558-1.217-1.232 C155.027-148.053,155.46-148.645,156.244-148.645z'
							></path>
							<path
								fill='#fff'
								d='M163.131-150.632c-2.052,0-3.274,1.56-3.274,3.232c0,1.902,1.479,3.203,3.281,3.203 c1.747,0,3.275-1.241,3.275-3.171C166.414-149.479,164.814-150.632,163.131-150.632z M163.151-148.645 c0.725,0,1.226,0.604,1.226,1.247c0,0.549-0.467,1.226-1.226,1.226c-0.695,0-1.217-0.558-1.217-1.232 C161.933-148.053,162.367-148.645,163.151-148.645z'
							></path>
							<circle cx='168.131' cy='-145.677' r='1.445' fill='#fff'></circle>
							<path
								fill='#fff'
								d='M170.05-147.653h-2.601l2.308-5.545h2.591L170.05-147.653z'
							></path>
						</g>
					</svg>
					<span>Yahoo</span>
				</Button>
			</div>
		</div>
	);
}
